/**
 * @fileoverview Institution Responsible Dashboard page component
 * Displays statistics, quick access buttons, and alerts for institution responsible users
 */

import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { fetchData, intl, HTTP_STATUS } from "dmsbasereact";

import { DashboardLayout } from "../../components/Dashboard";
import { APP_ROUTES } from "../../utils/NamedRoutes";
import { APP_COLORS } from "../../theme/appTheme";
import { canPrescribe, canDispense, hasStockFeatures } from "../../constants/InstitutionType";

// MUI Icons
import GroupIcon from "@mui/icons-material/Group";
import SchoolIcon from "@mui/icons-material/School";
import DescriptionIcon from "@mui/icons-material/Description";
import VaccinesIcon from "@mui/icons-material/Vaccines";
import BusinessIcon from "@mui/icons-material/Business";
import SettingsIcon from "@mui/icons-material/Settings";
import InventoryIcon from "@mui/icons-material/Inventory";
import InputIcon from "@mui/icons-material/Input";
import OutputIcon from "@mui/icons-material/Output";
import AssignmentReturnIcon from "@mui/icons-material/AssignmentReturn";

/**
 * InstitutionResponsibleDashboard - Dashboard page for institution responsible users
 * @returns {JSX.Element} The institution responsible dashboard with stats, quick access, and alerts
 */
export const InstitutionResponsibleDashboard = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState(null);
    const [alerts, setAlerts] = useState(null);
    const [institutionCategory, setInstitutionCategory] = useState(null);

    /**
     * Fetch all dashboard data from API endpoints
     */
    const fetchDashboardData = useCallback(async () => {
        setLoading(true);
        try {
            const [statsResponse, alertsResponse, institutionResponse] = await Promise.all([
                fetchData("/dashboard/institution/stats", "GET", true),
                fetchData("/dashboard/institution/alerts", "GET", true),
                fetchData("/get/organization_branch/by_person_pk", "GET", true).catch(() => null),
            ]);

            if (statsResponse.status === HTTP_STATUS.OK_200) {
                setStats(statsResponse.data?.data);
            }

            if (alertsResponse.status === HTTP_STATUS.OK_200) {
                setAlerts(alertsResponse.data?.data);
            }

            // Get institution category
            if (institutionResponse?.status === HTTP_STATUS.OK_200) {
                const responseData = institutionResponse.data?.data;
                // API returns either institution (single) or institutions (array)
                let inst = null;
                if (responseData?.institution) {
                    inst = responseData.institution;
                } else if (responseData?.institutions && responseData.institutions.length > 0) {
                    inst = responseData.institutions[0];
                }
                if (inst) {
                    const category = inst?.organization_branch?.category ?? inst?.category;
                    setInstitutionCategory(category);
                }
            }
        } catch (err) {
            console.error("Error fetching institution dashboard data:", err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchDashboardData();
    }, [fetchDashboardData]);

    /**
     * Format pending training alerts
     */
    const formatPendingTrainingAlerts = () => {
        if (!alerts?.pending_training) return [];
        return alerts.pending_training.map((item) => ({
            id: item.user_pk,
            title: item.user_name,
            description: `${intl.get("PendingTraining").d("Pending Training")}`,
            user_pk: item.user_pk,
        }));
    };

    /**
     * Format recent activity alerts
     */
    const formatRecentActivityAlerts = () => {
        if (!alerts?.recent_activity) return [];
        return alerts.recent_activity.map((item) => ({
            id: item.activity_pk,
            title: item.activity_description,
            description: new Date(item.activity_date).toLocaleDateString('pt-BR'),
            activity_pk: item.activity_pk,
        }));
    };

    /**
     * Handle click on pending training alert
     */
    const handleTrainingClick = (item) => {
        if (item?.user_pk) {
            navigate(APP_ROUTES.TrainingVideo);
        }
    };

    /**
     * Handle click on recent activity alert
     */
    const handleActivityClick = (item) => {
        if (item?.activity_pk) {
            navigate(APP_ROUTES.UserManagement);
        }
    };

    /**
     * Handle click on document alert - navigate to institution page documents tab
     */
    const handleDocumentClick = () => {
        navigate(APP_ROUTES.InstitutionCentral);
    };

    /**
     * Format expiring/expired document alerts from institution
     */
    const formatExpiringDocumentAlerts = () => {
        const items = [];

        // Add expired documents first (more urgent)
        if (alerts?.expired_documents?.length > 0) {
            alerts.expired_documents.forEach((doc) => {
                items.push({
                    id: `expired-${doc.file_pk}`,
                    title: doc.document_name,
                    description: intl.get("DocumentExpiredDaysAgo", { days: doc.days_expired })
                        .d("Expired {days} day(s) ago"),
                    severity: "error"
                });
            });
        }

        // Add expiring soon documents
        if (alerts?.expiring_documents?.length > 0) {
            alerts.expiring_documents.forEach((doc) => {
                items.push({
                    id: `expiring-${doc.file_pk}`,
                    title: doc.document_name,
                    description: intl.get("DocumentExpiresInDays", { days: doc.days_remaining })
                        .d("Expires in {days} day(s)"),
                    severity: "warning"
                });
            });
        }

        return items;
    };

    // Stats configuration - dynamic based on institution category
    const statsConfig = useMemo(() => {
        const config = [
            {
                title: intl.get("UsersInInstitution").d("Users in Institution"),
                value: stats?.users_in_institution || 0,
                icon: GroupIcon,
                color: APP_COLORS.primary.main,
                onClick: () => navigate(APP_ROUTES.UserManagement),
            },
            {
                title: intl.get("TrainingCompleted").d("Training Completed"),
                value: stats?.training_completed || 0,
                icon: SchoolIcon,
                color: APP_COLORS.system.success,
                onClick: () => navigate(APP_ROUTES.TrainingVideo),
            },
        ];

        // Add prescriptions stat only for prescriber institutions
        if (canPrescribe(institutionCategory)) {
            config.push({
                title: intl.get("PrescriptionsThisMonth").d("Prescriptions This Month"),
                value: stats?.prescriptions_this_month || 0,
                icon: DescriptionIcon,
                color: APP_COLORS.system.info,
            });
        }

        // Add dispensations stat only for dispenser institutions
        if (canDispense(institutionCategory)) {
            config.push({
                title: intl.get("DispensationsThisMonth").d("Dispensations This Month"),
                value: stats?.dispensations_this_month || 0,
                icon: VaccinesIcon,
                color: APP_COLORS.secondary.main,
            });
        }

        // Add stock-related stats for stock-enabled institutions
        if (hasStockFeatures(institutionCategory)) {
            config.push({
                title: intl.get("CheckInsThisMonth").d("Check-ins This Month"),
                value: stats?.check_ins_this_month || 0,
                icon: InputIcon,
                color: APP_COLORS.system.info,
                onClick: () => navigate(APP_ROUTES.CheckinCheckout),
            });
            config.push({
                title: intl.get("CheckOutsThisMonth").d("Check-outs This Month"),
                value: stats?.check_outs_this_month || 0,
                icon: OutputIcon,
                color: APP_COLORS.system.warning,
                onClick: () => navigate(APP_ROUTES.CheckinCheckout),
            });
            config.push({
                title: intl.get("CurrentStockBatches").d("Stock Batches"),
                value: stats?.current_stock_batches || 0,
                icon: InventoryIcon,
                color: APP_COLORS.primary.main,
                onClick: () => navigate(APP_ROUTES.InstitutionStock),
            });
            config.push({
                title: intl.get("PendingReturns").d("Pending Returns"),
                value: stats?.pending_returns || 0,
                icon: AssignmentReturnIcon,
                color: APP_COLORS.system.error,
            });
        }

        return config;
    }, [stats, institutionCategory, navigate]);

    // Quick access buttons configuration - dynamic based on institution category
    const quickAccessConfig = useMemo(() => {
        const config = [
            {
                label: intl.get("MyInstitution").d("My Institution"),
                icon: BusinessIcon,
                route: APP_ROUTES.InstitutionCentral,
                color: APP_COLORS.primary.main,
            },
            {
                label: intl.get("Training").d("Training"),
                icon: SchoolIcon,
                route: APP_ROUTES.TrainingVideo,
                color: APP_COLORS.system.info,
            },
        ];

        // Add stock-related quick access for stock-enabled institutions
        if (hasStockFeatures(institutionCategory)) {
            config.push({
                label: intl.get("Stock").d("Stock"),
                icon: InventoryIcon,
                route: APP_ROUTES.InstitutionStock,
                color: APP_COLORS.system.success,
            });
            config.push({
                label: intl.get("CheckInOut").d("Check-in/out"),
                icon: InputIcon,
                route: APP_ROUTES.CheckinCheckout,
                color: APP_COLORS.system.warning,
            });
        }

        config.push({
            label: intl.get("ProfileSettings").d("Profile Settings"),
            icon: SettingsIcon,
            route: APP_ROUTES.ProfileSettings,
            color: APP_COLORS.system.warning,
        });

        return config;
    }, [institutionCategory]);

    // Alerts configuration
    const alertsConfig = [];

    // Add document expiration alerts first (most important)
    const documentAlerts = formatExpiringDocumentAlerts();
    if (documentAlerts.length > 0) {
        const hasExpired = alerts?.expired_documents_count > 0;
        alertsConfig.push({
            title: hasExpired
                ? intl.get("DocumentsNeedAttention").d("Documents Need Attention")
                : intl.get("DocumentsExpiringSoon").d("Documents Expiring Soon"),
            items: documentAlerts,
            severity: hasExpired ? "error" : "warning",
            emptyMessage: "",
            onItemClick: handleDocumentClick,
        });
    }

    alertsConfig.push(
        {
            title: intl.get("PendingTraining").d("Pending Training"),
            items: formatPendingTrainingAlerts(),
            severity: "warning",
            emptyMessage: intl.get("NoPendingTraining").d("No pending training"),
            onItemClick: handleTrainingClick,
        },
        {
            title: intl.get("RecentActivity").d("Recent Activity"),
            items: formatRecentActivityAlerts(),
            severity: "info",
            emptyMessage: intl.get("NoRecentActivity").d("No recent activity"),
            onItemClick: handleActivityClick,
        }
    );

    const welcomeName = stats?.responsible_name && stats?.institution_name
        ? `${stats.responsible_name} - ${stats.institution_name}`
        : stats?.responsible_name || stats?.institution_name || null;

    return (
        <DashboardLayout
            welcomeName={welcomeName}
            loading={loading}
            stats={statsConfig}
            quickAccess={quickAccessConfig}
            alerts={alertsConfig}
        />
    );
};
